package com.heelion.farmafreelance.ui

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.RoundRect
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathFillType
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.boundsInRoot
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.heelion.farmafreelance.FarmaFreelanceBlue
import com.heelion.farmafreelance.models.UserRole

/** Le parti dell'app che il tour illumina (vedi [tourBersaglio]). */
enum class BersaglioTour { HOME, ANNUNCI, CERCA, CAMPANELLA, FARMY, PROFILO }

/** Posizione sullo schermo delle parti da illuminare, aggiornata mentre l'app si disegna. */
private val posizioniTour = mutableStateMapOf<BersaglioTour, Rect>()

/** Segna una parte dell'app come tappa del tour: il tour ne legge la posizione per illuminarla. */
fun Modifier.tourBersaglio(bersaglio: BersaglioTour): Modifier =
    onGloballyPositioned { posizioniTour[bersaglio] = it.boundsInRoot() }

private class PassoTour(val bersaglio: BersaglioTour?, val titolo: String, val testo: String)

/** Le tappe del tour, diverse per professionista (farmacista o infermiere) e farmacia. [nome] per il benvenuto. */
private fun passiTour(ruolo: UserRole?, nome: String?): List<PassoTour> {
    val farmacia = ruolo == UserRole.FARMACIA
    val saluto = if (nome.isNullOrBlank()) "Benvenuto su FarmaFreelance!" else "Benvenuto su FarmaFreelance, $nome!"
    return listOf(
        PassoTour(null, saluto, "Sono Farmy, il tuo assistente. In un minuto ti mostro come funziona l'app. Puoi saltare quando vuoi."),
        PassoTour(
            BersaglioTour.HOME, "La tua situazione",
            if (farmacia) "Qui vedi a colpo d'occhio le candidature ricevute, i turni da confermare, quelli in attesa e quelli confermati. Tocca un riquadro per filtrare l'elenco qui sotto."
            else "Qui vedi a colpo d'occhio le proposte ricevute, quelle da confermare, quelle in attesa e i turni confermati. Tocca un riquadro per filtrare l'elenco qui sotto."
        ),
        PassoTour(
            BersaglioTour.ANNUNCI, "Annunci",
            if (farmacia) "Qui pubblichi i turni da coprire: se cerchi un farmacista o un infermiere, giorni, orari, compenso e quante persone ti servono. Appena pubblichi, ti suggerisco io i più adatti."
            else "Qui pubblichi le tue disponibilità: i giorni e gli orari in cui puoi lavorare e il compenso che chiedi. Le farmacie della tua zona le vedranno."
        ),
        PassoTour(
            BersaglioTour.CERCA, "Cerca",
            if (farmacia) "Qui cerchi farmacisti e infermieri per zona e competenze e puoi fare loro una proposta diretta."
            else "Qui cerchi le farmacie della tua zona, vedi i turni che offrono e puoi candidarti."
        ),
        PassoTour(
            BersaglioTour.CAMPANELLA, "Notifiche",
            "Qui arrivano inviti, accettazioni e conferme. Quando c'è qualcosa di nuovo suona e lampeggia: puoi rispondere direttamente dalla notifica."
        ),
        PassoTour(
            BersaglioTour.FARMY, "Chiedi a me",
            "Tocca qui quando vuoi: scrivimi o parlami, per esempio \"quando è il mio prossimo turno?\" o \"avvisami quando c'è un turno a Roma il sabato\"."
        ),
        PassoTour(
            BersaglioTour.PROFILO, "Il tuo profilo",
            if (farmacia) "Completa i dati di fatturazione (P.IVA, SDI, PEC): servono per le fatture dei turni."
            else "Completa i dati fiscali e le competenze: servono per le fatture e per farti trovare dalle farmacie giuste."
        ),
        PassoTour(null, "Tutto pronto!", "Se vuoi rivedere questo tour, tocca il punto interrogativo in alto a sinistra. Buon lavoro!")
    )
}

/**
 * Tour di Farmy: oscura lo schermo e illumina una parte dell'app alla volta, con la spiegazione
 * in un fumetto (anche a voce). [onFine] quando l'utente arriva in fondo o salta.
 */
@Composable
fun TourFarmy(ruolo: UserRole?, nome: String?, onFine: () -> Unit) {
    val passi = remember(ruolo, nome) { passiTour(ruolo, nome) }
    var indice by remember { mutableStateOf(0) }
    val passo = passi[indice]
    val voce = LocalAssistente.current.voce
    val density = LocalDensity.current

    fun fine() {
        voce.stop()
        onFine()
    }

    LaunchedEffect(indice) { voce.speak("${passo.titolo}. ${passo.testo}") }

    val zona = passo.bersaglio?.let { posizioniTour[it] }
    BoxWithConstraints(
        Modifier.fillMaxSize()
            // Blocca i tocchi sull'app sotto: durante il tour si usano solo Avanti e Salta.
            .pointerInput(Unit) { detectTapGestures { } }
    ) {
        val margine = with(density) { 8.dp.toPx() }
        Canvas(Modifier.fillMaxSize()) {
            val buco = zona?.inflate(margine)
            val ombra = Path().apply {
                fillType = PathFillType.EvenOdd
                addRect(Rect(0f, 0f, size.width, size.height))
                if (buco != null) addRoundRect(RoundRect(buco, CornerRadius(margine * 2)))
            }
            drawPath(ombra, Color.Black.copy(alpha = 0.6f))
            if (buco != null) {
                drawRoundRect(
                    color = Color(0xFFFFD600),
                    topLeft = buco.topLeft, size = buco.size,
                    cornerRadius = CornerRadius(margine * 2), style = Stroke(width = margine / 2)
                )
            }
        }

        // Il fumetto sta dalla parte opposta alla zona illuminata, per non coprirla.
        val altezzaSchermo = with(density) { maxHeight.toPx() }
        val inBasso = zona == null || zona.center.y < altezzaSchermo / 2
        Card(
            modifier = Modifier
                .align(if (zona == null) Alignment.Center else if (inBasso) Alignment.BottomCenter else Alignment.TopCenter)
                .padding(horizontal = 16.dp, vertical = 110.dp)
                .widthIn(max = 480.dp),
            shape = RoundedCornerShape(16.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White)
        ) {
            Column(Modifier.padding(16.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    AvatarAssistente(Modifier.size(40.dp))
                    Spacer(Modifier.width(10.dp))
                    Text(passo.titolo, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = FarmaFreelanceBlue)
                }
                Spacer(Modifier.height(8.dp))
                Text(passo.testo, style = MaterialTheme.typography.bodyMedium)
                Spacer(Modifier.height(12.dp))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text("${indice + 1} di ${passi.size}", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                    Spacer(Modifier.weight(1f))
                    if (indice < passi.size - 1) {
                        TextButton(onClick = ::fine) { Text("Salta") }
                        if (indice > 0) TextButton(onClick = { indice-- }) { Text("Indietro") }
                        Button(onClick = { indice++ }) { Text("Avanti") }
                    } else {
                        if (indice > 0) TextButton(onClick = { indice-- }) { Text("Indietro") }
                        Button(onClick = ::fine) { Text("Inizia") }
                    }
                }
            }
        }
    }
}
